import React, { ReactNode } from 'react';
import {
  Box,
  Button,
  InputLabel,
  Modal,
  Select,
  StackItem,
  StackLayout,
  TextAction,
  TextInput,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  EVENT_NAME,
  PRODUCT_AREA,
  SCREEN_NAMES,
  SUB_PRODUCT_AREA,
  trackAnalyticsEvent,
} from '@leagueplatform/analytics';
import { DETAIL } from '../../common/constants';

type SelectOption = {
  id?: string;
  value: string;
  label: string;
};

type FieldConfig = {
  id: string;
  name: string;
  label: string;
  placeholder?: string;
  value?: string | string[];
  editable?: boolean;
  type?: string;
  required?: boolean;
  options?: SelectOption[];
  tooltip?: {
    text: string;
    type: string;
    component?: React.FC;
  };
};

type UserProfileEditableFormProps = {
  fields: FieldConfig[];
  handleSave: (inputValue: Record<string, any>) => Promise<void> | undefined;
  isLoading?: boolean;
  // TODO: fix
  formState: any;
  showAdditionalSettings?: boolean;
  additionalSettingsFooterLink?: string;
  formSubmission?: boolean;
  // New prop for page context
  pageContext?: {
    productArea: string;
    screenName: string;
    subProductArea: string;
  };
};

type FormWrapperProps = {
  handleSave: (inputValue: Record<string, any>) => Promise<void> | undefined; // Function to handle save
  formSubmission: boolean; // Whether the form is in submission mode
  inputValue: Record<string, any>; // The current input values
  children: ReactNode; // The child components to render inside the wrapper
};

const FormWrapper: React.FC<FormWrapperProps> = ({
  handleSave,
  formSubmission,
  inputValue,
  children,
}) => {
  if (formSubmission) {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          handleSave(inputValue);
        }}
      >
        {children}
      </form>
    );
  }
  return <Box>{children}</Box>;
};

export const UserProfileEditableForm: React.FC<
  UserProfileEditableFormProps
> = ({
  fields,
  handleSave,
  isLoading,
  formState,
  showAdditionalSettings = false,
  additionalSettingsFooterLink,
  formSubmission = false,
  pageContext = {
    productArea: PRODUCT_AREA.ONBOARDING,
    screenName: SCREEN_NAMES.ACCOUNT_SET_UP,
    subProductArea: SUB_PRODUCT_AREA.ACCOUNT_SET_UP,
  },
}) => {
  const { formatMessage } = useIntl();

  const { inputValue, allowSave, handleInputChange, originalValues } =
    formState;

  const [activeModal, setActiveModal] = React.useState<React.FC | null>(null);

  const handleSaveWithAnalytics = async (values: Record<string, any>) => {
    // Check if preferred name was changed
    if (values.preferredFirstName !== originalValues.preferredFirstName) {
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: pageContext.productArea,
        sub_product_area: pageContext.subProductArea,
        screen_name: pageContext.screenName,
        detail: DETAIL.PREFERRED_NAME_UPDATED,
      });
    }

    // Check if gender identity was changed
    if (values.genderIdentity !== originalValues.genderIdentity) {
      trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
        product_area: pageContext.productArea,
        sub_product_area: pageContext.subProductArea,
        screen_name: pageContext.screenName,
        detail: DETAIL.GENDER_IDENTITY_UPDATED,
      });
    }

    // Call the original save handler
    return handleSave(values);
  };

  const handleLinkClick = (link: any) => {
    if (link.type === 'modal') {
      // Open external link
      setActiveModal(() => link.component);

      // TODO: Add Open external link
      // TODO: Add navigate to route
    }
  };

  return (
    <Box css={{ width: '100%' }}>
      <FormWrapper
        formSubmission={formSubmission}
        handleSave={handleSaveWithAnalytics}
        inputValue={inputValue}
      >
        {/* Tried using FormField for genesis-core but wasn't able to handle the position for linkout for why do you ask?   */}
        {fields.map((field) => (
          <Box key={field.id} css={{ marginBottom: '$one' }}>
            <StackLayout
              orientation="horizontal"
              horizontalAlignment="spaceBetween"
              css={{ width: '100%' }}
            >
              <InputLabel
                htmlFor={field.id}
                required={field?.required}
                css={{ typography: '$label' }}
              >
                {field.label}
              </InputLabel>

              {field.tooltip && (
                // TODO: Make target and rel a prop
                <TextAction
                  as="button"
                  onClick={() => handleLinkClick(field.tooltip)}
                  css={{ typography: '$label' }}
                >
                  {field.tooltip.text}
                </TextAction>
              )}
            </StackLayout>
            <StackLayout css={{ width: '100%' }}>
              {field.type === 'select' ? (
                <Select
                  id={field.id}
                  name={field.name}
                  // TODO: Make aria Label dynamic
                  aria-label="Select Gender Identity"
                  options={field.options || []}
                  onChange={(e) =>
                    handleInputChange(
                      field.id,
                      (e.target as HTMLSelectElement).value,
                    )
                  }
                  value={inputValue[field.id]}
                  disabled={!field.editable}
                  aria-disabled={!field.editable}
                  css={{
                    width: '100%',
                    ...(!field.editable && {
                      backgroundColor: '$inputBackgroundDisabled',
                    }),
                  }}
                />
              ) : (
                <StackItem css={{ width: '100%' }}>
                  <TextInput
                    id={field.id}
                    name={field.name}
                    onChange={(e) =>
                      handleInputChange(field.id, e.target.value)
                    }
                    type={field.type || 'text'}
                    value={inputValue[field.id]}
                    placeholder={field.placeholder}
                    disabled={!field.editable}
                    css={{
                      ...(!field.editable && {
                        backgroundColor: '$inputBackgroundDisabled',
                      }),
                    }}
                  />
                </StackItem>
              )}
            </StackLayout>
          </Box>
        ))}
        <StackLayout
          horizontalAlignment={{
            '@initial': 'spaceBetween',
            '@mobile': 'center',
          }}
          verticalAlignment="center"
          orientation={{
            '@initial': 'horizontal',
            '@mobile': 'vertical',
          }}
        >
          {showAdditionalSettings && (
            <TextAction
              size="medium"
              as="a"
              target="_blank"
              rel="noopener noreferrer"
              indicator="externalLink"
              href={additionalSettingsFooterLink}
              css={{ marginLeft: '$twoAndHalf' }}
              onClick={() => {
                trackAnalyticsEvent(EVENT_NAME.BUTTON_CLICKED, {
                  product_area: PRODUCT_AREA.SETTINGS,
                  sub_product_area: SUB_PRODUCT_AREA.PERSONAL_INFO,
                  screen_name: SCREEN_NAMES.PERSONAL_INFO,
                  detail: DETAIL.EDIT_SETTINGS_LINK_OUT,
                });
              }}
            >
              {formatMessage({ id: 'STR_EDIT_ADDITIONAL_SETTINGS' })}
            </TextAction>
          )}
          {formSubmission && (
            <Button
              loading={isLoading}
              type="submit"
              width={{
                '@initial': 'hugContents',
                '@mobile': 'fillContainer',
              }}
              disabled={!allowSave}
              css={{
                marginTop: '$one',
              }}
            >
              {formatMessage({ id: 'STR_SAVE' })}
            </Button>
          )}
        </StackLayout>
        {activeModal && (
          <Modal.Root open onOpenChange={() => setActiveModal(null)}>
            {React.createElement(activeModal)}
          </Modal.Root>
        )}
      </FormWrapper>
    </Box>
  );
};
